import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

//assets
import {
  IconArrowsExpand,
  IconSocialFacebook,
  IconSocialInstagram,
  IconSocialYoutube,
  IconSocialTwitter,
  IconSocialLinkedin,
} from '@loadsmart/icons';
import LoadsmartLogo from '@/public/images/logos/loadsmart_logotype.svg';

//components
import { LangButton } from '@/components/LangButton';

//data-helpers
import { getTranslationsForPath } from '@/datafiles/i18n/translatedPages';
import { LOCALES } from '@/datafiles/i18n/locales';

//styles
import {
  ContainerWrapper,
  MainContent,
  LogoWrapper,
  LogoContent,
  NavWrapper,
  NavSegments,
  NavSegmentsGroup,
  SegmentLabel,
  SegmentSubItems,
  NavIcons,
  NavSocials,
  NavApps,
  LegalWrapper,
  CopyRightWrapper,
  Spacer,
  BottomRow,
  LanguageWrapper,
  LanguageButton,
  LanguageList,
  LanguageOption,
} from './Footer.styles';

const defaultMenuItems = [
  {
    label: 'Platform',
    cta: { url: '/platform/' },
    submenu: [
      {
        label: 'Loadsmart AI',
        url: '/',
        external: false,
      },
      {
        label: 'Loadsmart Platform',
        url: '/platform/',
        external: false,
      },
      {
        label: 'Transportation Management System',
        url: 'https://shipperguide.com/?utm_source=loadsmart-website',
        external: true,
      },
      {
        label: <>Dock, Gate &amp; Yard</>,
        url: `${process.env.OPENDOCK_URL}/`,
        external: true,
      },
    ],
  },
  {
    label: 'Freight Services',
    cta: { url: '/shipper/' },
    submenu: [
      {
        label: 'Managed Transportation',
        url: '/shipper/managed-transportation/',
        external: false,
      },
      {
        label: 'Freight Brokerage',
        url: '/shipper/',
        external: false,
      },
      {
        label: 'Private Fleet Optimizer',
        url: 'https://lp.loadsmart.com/loadsmart-fleet-optimization',
        external: true,
      },
      {
        label: 'Flatbed Messenger',
        url: '/shipper/flatbed-messenger/',
        external: false,
      },
      {
        label: 'Expedited Shipping',
        url: '/shipper/expedited-shipping/',
        external: false,
      },
      {
        label: <>FTL &middot; Drayage &middot; Multimodal</>,
        url: '/mode-optimization/',
        external: false,
      },
    ],
  },
  {
    label: 'Carriers',
    cta: { url: '/carrier/' },
    submenu: [
      {
        label: 'Carrier Solutions',
        url: '/carrier/',
        external: false,
      },
      {
        label: 'Carrier Loadboard',
        url: `https://app.carrierguide.com/login`,
        external: true,
      },
      {
        label: 'CarrierGuide',
        url: '/carrier/tms/',
        external: false,
      },
      {
        label: 'Factoring',
        url: 'https://lp.loadsmart.com/factoring-sign-up',
        external: true,
      },
    ],
  },
  {
    label: 'Company',
    cta: { url: '/about/' },
    submenu: [
      {
        label: 'Who we are',
        url: '/about/',
        external: false,
      },
      {
        label: 'Careers',
        url: '/careers/',
        external: false,
      },
      {
        label: 'Partnerships',
        url: 'https://lp.loadsmart.com/loadsmart-partner-portal-1',
        external: true,
      },
      {
        label: 'Resources',
        url: 'https://content.loadsmart.com/',
        external: true,
      },
      {
        label: 'Blog',
        url: 'https://blog.loadsmart.com/',
        external: true,
      },
      {
        label: 'Contact',
        url: '/contact/',
        external: false,
      },
      {
        label: 'Community',
        url: 'https://community.loadsmart.com/hc/en-us',
        external: true,
      },
      {
        label: 'Loadsmart API',
        url: 'https://developer.loadsmart.com/docs/',
        external: true,
      },
      {
        label: 'Integrations',
        url: '/integrations/',
        external: false,
      },
      {
        label: 'Sightline',
        url: 'https://sightline.loadsmart.com/',
        external: true,
      },
    ],
  },
];

const defaultLegalItems = [
  {
    label: 'User Agreement',
    cta: { url: '/user-agreement', external: false },
  },
  {
    label: 'Subscription and Professional Services Agreement',
    cta: {
      url: '/subscription-and-professional-services-agreement',
      external: false,
    },
  },
  {
    label: 'Managed Transportation Master Services Agreement',
    cta: {
      url: '/managed-transportation-master-services-agreement',
      external: false,
    },
  },
  {
    label: 'Privacy Policy',
    cta: { url: '/privacy-policy', external: false },
  },
  {
    label: 'Security',
    cta: { url: 'https://lp.loadsmart.com/security', external: true },
  },
];

const socialItems = [
  {
    service: 'LinkedIn',
    user: '@loadsmart',
    url: 'https://www.linkedin.com/company/loadsmart',
    icon: <IconSocialLinkedin width="52" height="52" />,
  },
  {
    service: 'Instagram',
    user: '@loadsmart',
    url: 'https://www.instagram.com/loadsmart',
    icon: <IconSocialInstagram width="52" height="52" />,
  },
  {
    service: 'Twitter',
    user: '@LoadSmartUS',
    url: 'https://twitter.com/LoadSmartUS/',
    icon: <IconSocialTwitter width="52" height="52" />,
  },
  {
    service: 'YouTube',
    user: '@Loadsmart',
    url: 'https://www.youtube.com/channel/UCN-pkKcoMargmyL0Br3-H5w',
    icon: <IconSocialYoutube width="52" height="52" />,
  },
  {
    service: 'Facebook',
    user: '@loadsmartUS',
    url: 'https://www.facebook.com/loadsmartUS',
    icon: <IconSocialFacebook width="52" height="52" />,
  },
];

export function Footer({ theme, config }) {
  const currentYear = new Date().getFullYear();
  const menuItems = config?.columns ?? defaultMenuItems;
  const legalItems = config?.legal ?? defaultLegalItems;
  const homeUrl = config?.homeUrl ?? '/';
  const copyright = config
    ? config.copyright.replace('{year}', currentYear)
    : `© ${currentYear} Loadsmart Inc. All rights reserved. SOC 2 Type II.`;

  const router = useRouter();
  const translations = getTranslationsForPath(router.asPath);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  useEffect(() => {
    if (!langOpen) return undefined;

    const closeOnOutsideClick = (event) => {
      if (langRef.current && !langRef.current.contains(event.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('click', closeOnOutsideClick);
    return () => document.removeEventListener('click', closeOnOutsideClick);
  }, [langOpen]);

  return (
    <ContainerWrapper theme={theme}>
      <MainContent id="main-menu-wrapper">
        <LogoWrapper
          href={homeUrl}
          aria-label={config?.homeAriaLabel ?? 'Home'}
        >
          <LogoContent>
            <LoadsmartLogo width="263.01" height="44.23" />
          </LogoContent>
        </LogoWrapper>
        <NavWrapper>
          <NavSegments>
            {menuItems.map((item, index) => {
              const keyNav = `navmenu-${item.label.trim()}-${index}`;
              return (
                <NavSegmentsGroup key={keyNav}>
                  <SegmentLabel
                    {...(item.cta?.url && { href: item.cta.url, as: 'a' })}
                  >
                    {item.label}
                  </SegmentLabel>
                  <SegmentSubItems>
                    {item.submenu.map((subitem, index) => {
                      const keyNavSub = `navmenu-${item.label}-sub-${index}`;
                      return (
                        <a
                          key={keyNavSub}
                          {...(subitem.url && { href: subitem.url })}
                          {...(subitem.external && { target: '_blank' })}
                          {...(subitem.external && {
                            rel: 'noreferrer noopener',
                          })}
                        >
                          {subitem.label}
                        </a>
                      );
                    })}
                  </SegmentSubItems>
                </NavSegmentsGroup>
              );
            })}
          </NavSegments>
          <NavIcons>
            <NavSocials>
              {socialItems.map((item, index) => {
                const keyNav = `navmenu-${item.service.trim()}-${index}`;
                return (
                  <a
                    key={keyNav}
                    href={item.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    title={item.user}
                  >
                    {item.icon}
                  </a>
                );
              })}
            </NavSocials>
          </NavIcons>
        </NavWrapper>
        <Spacer />
        <LegalWrapper>
          {legalItems.map((item, index) => {
            const keyNav = `navmenu-${item.label.trim()}-${index}`;
            return (
              <a
                key={keyNav}
                {...(item.cta?.url && { href: item.cta.url })}
                {...(item.cta?.external && { target: '_blank' })}
                {...(item.cta?.external && { rel: 'noreferrer noopener' })}
              >
                {item.label}
              </a>
            );
          })}
        </LegalWrapper>
        <BottomRow>
          <CopyRightWrapper>{copyright}</CopyRightWrapper>
          {translations && (
            <LanguageWrapper ref={langRef}>
              <LanguageList className={langOpen ? 'open' : ''}>
                {Object.keys(translations.entry).map((locale) => (
                  <LanguageOption
                    key={locale}
                    href={translations.entry[locale]}
                    hrefLang={LOCALES[locale].hreflang}
                    className={
                      translations.currentLocale === locale ? 'current' : ''
                    }
                  >
                    <span>{LOCALES[locale].label}</span>
                    <LangButton label={LOCALES[locale].flag} />
                  </LanguageOption>
                ))}
              </LanguageList>
              <LanguageButton
                type="button"
                className={langOpen ? 'open' : ''}
                aria-expanded={langOpen}
                onClick={() => setLangOpen(!langOpen)}
              >
                <LangButton label={LOCALES[translations.currentLocale].flag} />
                <span>{LOCALES[translations.currentLocale].label}</span>
                <IconArrowsExpand width="16" height="16" />
              </LanguageButton>
            </LanguageWrapper>
          )}
        </BottomRow>
      </MainContent>
    </ContainerWrapper>
  );
}
